import React from 'react';
import { Box, Tabs, Tab, Chip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useOpenSearchTabs } from '@/hooks/useOpenSearchTabs';
import { SearchTabCounts } from '../types';
import { SearchTenantMenu } from '../SearchTenantMenu';
import { TenantUserSettings } from '@/api/generatedApi';

const TabsContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '40px',
  '& .MuiTabs-flexContainer': {
    gap: 0,
  },
  '& .MuiTab-root': {
    minHeight: '40px',
    height: '40px',
    backgroundColor: theme.palette.background.default,
    border: 'none',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 400,
    padding: '0 12px 0 20px',
    minWidth: 'auto',
    marginRight: '0px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

interface SearchTabsProperties {
  resultCounts?: SearchTabCounts;
  loadingStates?: Record<string, boolean>;
}

const CountChip = styled(Chip)(() => ({
  height: '18px',
  fontSize: '11px',
  fontWeight: 500,
  marginLeft: '6px',
  minWidth: '24px',
  maxWidth: 'none',
  '& .MuiChip-label': {
    padding: '0 6px',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    maxWidth: 'none',
  },
}));

const CloseButton = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  marginLeft: '4px',
  borderRadius: '50%',
  cursor: 'pointer',
  opacity: 0,
  transition: 'opacity 0.15s ease-in-out, background-color 0.15s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '.MuiTab-root:hover &, .MuiTab-root.Mui-selected &': {
    opacity: 1,
  },
}));

const TabLabel = ({
  name,
  count,
  isLoading,
  showCount,
  onClose,
}: {
  name: string;
  count?: number;
  isLoading?: boolean;
  showCount: boolean;
  onClose: (event: React.MouseEvent) => void;
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {name}
      {showCount &&
        (isLoading ? (
          <Box
            sx={{ marginLeft: '6px', width: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <CircularProgress size={18} thickness={4} sx={{ color: 'text.primary' }} />
          </Box>
        ) : (
          count !== undefined && <CountChip size='small' label={count} />
        ))}
      <CloseButton
        role='button'
        tabIndex={0}
        onClick={onClose}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            onClose(event as unknown as React.MouseEvent);
          }
        }}
        data-testid={SearchPageObjectModel.SearchTabCloseButton}
        aria-label='Close tab'
      >
        <Close sx={{ fontSize: '14px' }} />
      </CloseButton>
    </Box>
  );
};

export const SearchTabs = ({ resultCounts, loadingStates }: SearchTabsProperties) => {
  const { selectedSearchTab, handleSelectSearchTab, clearSearchTabSelection, shouldShowSearchTabs } = useSearchTabs();
  const { openTabs, availableTenants, addTab, removeTab, hasOpenTabs } = useOpenSearchTabs();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const clickedTenant = openTabs[newValue];
    if (clickedTenant) {
      handleSelectSearchTab(clickedTenant);
    }
  };

  const handleCloseTab = (event: React.MouseEvent, tenantId: string) => {
    event.stopPropagation();
    const isCurrentTab = selectedSearchTab?.tenantId === tenantId;
    const remainingTabs = openTabs.filter(t => t?.tenantId !== tenantId);

    removeTab(tenantId);

    if (isCurrentTab) {
      if (remainingTabs.length > 0) {
        handleSelectSearchTab(remainingTabs[0]);
      } else {
        clearSearchTabSelection();
      }
    }
  };

  const handleAddTenant = (tenant: TenantUserSettings) => {
    addTab(tenant);
    handleSelectSearchTab(tenant);
  };

  if (!shouldShowSearchTabs) {
    return;
  }

  const showCounts = openTabs.length > 1;
  const validActiveTab = openTabs.findIndex(tenant => tenant?.tenantId === selectedSearchTab?.tenantId);
  const activeTabIndex = validActiveTab === -1 ? 0 : validActiveTab;

  return (
    <TabsContainer data-testid={SearchPageObjectModel.SearchTabs}>
      {hasOpenTabs && (
        <StyledTabs value={activeTabIndex} onChange={handleTabChange}>
          {openTabs.map((tenant, index) => {
            const tenantId = tenant?.tenantId ?? '';

            return (
              <Tab
                key={tenantId}
                label={
                  <TabLabel
                    name={tenant?.tenantDisplayName ?? ''}
                    count={resultCounts?.[tenantId]}
                    isLoading={loadingStates?.[tenantId]}
                    showCount={showCounts}
                    onClose={event => handleCloseTab(event, tenantId)}
                  />
                }
                value={index}
              />
            );
          })}
        </StyledTabs>
      )}
      <SearchTenantMenu
        tenants={availableTenants}
        resultCounts={resultCounts}
        loadingStates={loadingStates}
        onSelectTenant={handleAddTenant}
      />
    </TabsContainer>
  );
};
